/**
 * Invite Codes — List and manage invitation codes
 *
 * Features:
 * - List all invite codes with status, role, expiry
 * - Create new invites via InviteModal
 * - Revoke/resend actions
 * - Filter by status (active/used/expired/revoked)
 * - CASL permission-gated
 */

import { useState, useMemo, useCallback } from "react";
import { Chip, IconButton, Tooltip } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ListPageTemplate from "templates/ListPageTemplate";
import KDBox from "components/KDBox";
import KDButton from "components/KDButton";
import KDSnackbar from "components/KDSnackbar";
import useInviteCodes from "hooks/useInviteCodes";
import { useT } from "context/I18nContext";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";
import InviteModal from "layouts/users/components/InviteModal";

function InviteCodes() {
  const t = useT();
  const ability = useAbility();
  const { invites, loading, error, revokeInvite, resendEmail, fetchInvites } = useInviteCodes({
    autoFetch: true,
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, color: "info", title: "", content: "" });
  const [includeUsed, setIncludeUsed] = useState(false);

  const canCreate = ability.can(ACTIONS.INVITE, SUBJECTS.USER);
  const canDelete = ability.can(ACTIONS.DELETE, SUBJECTS.INVITE_CODE);

  const showSnackbar = (color, title, content = "") => {
    setSnackbar({ open: true, color, title, content });
  };

  const handleCopyCode = useCallback(
    (code) => {
      navigator.clipboard.writeText(code);
      showSnackbar("success", t("invite.copied"));
    },
    [t]
  );

  const handleRevoke = useCallback(
    async (id) => {
      try {
        await revokeInvite(id);
        showSnackbar("success", t("inviteCodes.revoked"));
      } catch (err) {
        showSnackbar("error", t("common.error"), err.message);
      }
    },
    [revokeInvite, t]
  );

  const handleResend = useCallback(
    async (id) => {
      try {
        await resendEmail(id);
        showSnackbar("success", t("invite.emailSent"));
      } catch (err) {
        showSnackbar("error", t("common.error"), err.message);
      }
    },
    [resendEmail, t]
  );

  const handleInviteClose = useCallback(() => {
    setInviteOpen(false);
    fetchInvites(includeUsed);
  }, [fetchInvites, includeUsed]);

  const getStatus = (invite) => {
    if (invite.revokedAt) return "revoked";
    if (invite.usedAt || invite.usedBy) return "used";
    if (invite.expiresAt) {
      const exp =
        invite.expiresAt._seconds
          ? new Date(invite.expiresAt._seconds * 1000)
          : new Date(invite.expiresAt);
      if (exp < new Date()) return "expired";
    }
    return "active";
  };

  const statusColors = {
    active: "success",
    used: "info",
    expired: "warning",
    revoked: "error",
  };

  const columns = useMemo(
    () => [
      {
        field: "code",
        header: t("inviteCodes.code"),
        width: "18%",
        render: (val) => (
          <KDBox display="flex" alignItems="center" gap={0.5}>
            <code style={{ fontSize: "0.8rem" }}>{val || "—"}</code>
            {val && (
              <Tooltip title={t("invite.copyCode")}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopyCode(val); }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </KDBox>
        ),
      },
      {
        field: "email",
        header: t("common.email"),
        width: "20%",
        render: (val) => val || "—",
      },
      {
        field: "role",
        header: t("common.role"),
        width: "12%",
        render: (val) => (
          <Chip
            label={val || "—"}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          />
        ),
      },
      {
        field: "accessLevel",
        header: t("invite.accessLevel"),
        width: "12%",
        render: (val) => val || "—",
      },
      {
        field: "_status",
        header: t("common.status"),
        width: "10%",
        render: (_, row) => {
          const status = getStatus(row);
          return (
            <Chip
              label={t(`inviteCodes.status_${status}`)}
              size="small"
              color={statusColors[status]}
            />
          );
        },
      },
      {
        field: "createdAt",
        header: t("users.createdAt"),
        width: "13%",
        render: (val) => {
          if (!val) return "—";
          const d = val._seconds ? new Date(val._seconds * 1000) : new Date(val);
          return d.toLocaleDateString();
        },
      },
      {
        field: "actions",
        header: t("common.actions"),
        width: "15%",
        align: "right",
        sortable: false,
        render: (_, row) => {
          const status = getStatus(row);
          return (
            <KDBox display="flex" justifyContent="flex-end" gap={0.5}>
              {status === "active" && row.email && (
                <Tooltip title={t("inviteCodes.resend")}>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => { e.stopPropagation(); handleResend(row.id); }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {status === "active" && canDelete && (
                <Tooltip title={t("inviteCodes.revoke")}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => { e.stopPropagation(); handleRevoke(row.id); }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </KDBox>
          );
        },
      },
    ],
    [t, canDelete, handleCopyCode, handleResend, handleRevoke]
  );

  return (
    <>
      <ListPageTemplate
        title={t("nav.invites")}
        columns={columns}
        rows={invites}
        loading={loading}
        error={error}
        emptyMessage={t("inviteCodes.empty")}
        searchable
        searchPlaceholder={t("inviteCodes.search")}
        defaultSortField="createdAt"
        defaultSortDirection="desc"
        headerColor="primary"
        defaultVisibleFields={["code", "email", "role", "accessLevel", "_status", "createdAt", "actions"]}
        extraToolbarActions={
          canCreate && (
            <KDButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setInviteOpen(true)}
              sx={{
                borderRadius: "10px",
                background: "linear-gradient(135deg, #0D9488 0%, #5EEAD4 100%)",
                "&:hover": { background: "linear-gradient(135deg, #0B8276 0%, #4DD9C7 100%)" },
              }}
            >
              <PersonAddIcon sx={{ fontSize: 18, mr: 0.5 }} />
              {t("invite.createCode")}
            </KDButton>
          )
        }
      />

      <InviteModal open={inviteOpen} onClose={handleInviteClose} />

      <KDSnackbar
        color={snackbar.color}
        icon="check"
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        close={() => setSnackbar({ ...snackbar, open: false })}
        bgWhite
      />
    </>
  );
}

export default InviteCodes;
